import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  createRepair,
  deleteRepair,
  fetchMainParts,
  fetchRepairs,
  fetchStockParts,
  updateRepair,
} from '../../api/manufacturingEquipment';
import {
  EquipmentRepair,
  EquipmentRepairPayload,
  ManufacturingStockPart,
} from '../../types/manufacturingEquipment';
import './EquipTabPanel.css';

import { reportError } from '../../utils/notify';
import { formatNumberDisplay, formatNumberInput, parseNumberInput } from '../../utils/numberFormat';
interface Props {
  equipmentId: number;
}

const EMPTY: EquipmentRepairPayload = {
  repairDate: '',
  partArea: '',
  details: '',
  cost: null,
  vendor: '',
  stockPartId: null,
  usedQty: 1,
};

function RepairsTab({ equipmentId }: Props) {
  const [rows, setRows] = useState<EquipmentRepair[]>([]);
  const [stockParts, setStockParts] = useState<ManufacturingStockPart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<EquipmentRepairPayload>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [repairs, stocks, mainParts] = await Promise.all([
        fetchRepairs(equipmentId),
        fetchStockParts(),
        fetchMainParts(equipmentId),
      ]);
      setRows(repairs);
      const mainStockIds = new Set(
        mainParts.map((item) => item.stockPartId).filter((id): id is number => id != null)
      );
      // 이 설비 주요 부속품 우선, 없으면 전체 보유 목록
      const preferred = stocks.filter((item) => mainStockIds.has(item.stockPartId));
      setStockParts(preferred.length > 0 ? preferred : stocks);
      setError('');
    } catch {
      reportError(setError, '교체수리 이력을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (row: EquipmentRepair) => {
    setEditingId(row.repairId);
    setForm({
      repairDate: row.repairDate ?? '',
      partArea: row.partArea ?? '',
      details: row.details ?? '',
      cost: row.cost ?? null,
      vendor: row.vendor ?? '',
      stockPartId: row.stockPartId ?? null,
      usedQty: row.usedQty ?? 1,
    });
    setShowForm(true);
  };

  const applyStockPart = (stockPartId: number | null) => {
    const stock = stockParts.find((item) => item.stockPartId === stockPartId);
    setForm((prev) => ({
      ...prev,
      stockPartId,
      partArea: stock ? stock.partName : prev.partArea,
      details: stock && !prev.details ? '교체' : prev.details,
      usedQty: stockPartId ? prev.usedQty || 1 : null,
    }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      const payload: EquipmentRepairPayload = {
        repairDate: form.repairDate || null,
        partArea: form.partArea?.trim() || undefined,
        details: form.details?.trim() || undefined,
        cost: form.cost == null || form.cost === ('' as unknown as number) ? null : Number(form.cost),
        vendor: form.vendor?.trim() || undefined,
        stockPartId: form.stockPartId || null,
        usedQty: form.stockPartId ? Number(form.usedQty) || 1 : null,
      };
      if (editingId == null) {
        await createRepair(equipmentId, payload);
      } else {
        await updateRepair(equipmentId, editingId, payload);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: EquipmentRepair) => {
    if (!window.confirm('이 교체수리 이력을 삭제할까요? (사용 재고는 복구됩니다)')) return;
    try {
      await deleteRepair(equipmentId, row.repairId);
      await load();
    } catch {
      reportError(setError, '삭제에 실패했습니다.');
    }
  };

  return (
    <div className="equip-tab-panel">
      <div className="equip-tab-panel__toolbar">
        <div>
          <h3>교체수리 이력</h3>
          <p className="equip-tab-panel__hint">
            이 설비의 주요 부속품(보유 재고)을 선택하면 수량이 차감됩니다.
          </p>
        </div>
        <button type="button" onClick={openCreate}>
          추가
        </button>
      </div>
      {error && <p className="equip-tab-panel__error">{error}</p>}
      {showForm && (
        <form className="equip-tab-panel__form" onSubmit={handleSubmit}>
          <label>
            일자
            <input
              type="date"
              value={form.repairDate ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, repairDate: e.target.value }))}
            />
          </label>
          <label className="is-span-2">
            보유 부속품 사용
            <select
              value={form.stockPartId ?? ''}
              onChange={(e) =>
                applyStockPart(e.target.value ? Number(e.target.value) : null)
              }
            >
              <option value="">사용 안 함</option>
              {stockParts.map((item) => (
                <option key={item.stockPartId} value={item.stockPartId}>
                  {item.partName}
                  {item.spec ? ` (${item.spec})` : ''} · 재고 {item.stockQty}
                </option>
              ))}
            </select>
          </label>
          <label>
            사용 수량
            <input
              type="text"
              inputMode="decimal"
              disabled={!form.stockPartId}
              value={form.usedQty == null ? '' : formatNumberDisplay(form.usedQty)}
              onChange={(e) => {
                const parsed = parseNumberInput(formatNumberInput(e.target.value));
                setForm((p) => ({
                  ...p,
                  usedQty: parsed == null ? null : Math.max(1, parsed),
                }));
              }}
            />
          </label>
          <label>
            부위
            <input
              value={form.partArea ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, partArea: e.target.value }))}
              placeholder="예: 컷팅날"
            />
          </label>
          <label>
            내역
            <input
              value={form.details ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, details: e.target.value }))}
              placeholder="예: 교체"
            />
          </label>
          <label>
            비용
            <input
              type="text"
              inputMode="decimal"
              value={form.cost == null ? '' : formatNumberDisplay(form.cost)}
              onChange={(e) => {
                const formatted = formatNumberInput(e.target.value);
                const parsed = parseNumberInput(formatted);
                setForm((p) => ({
                  ...p,
                  cost: parsed == null ? null : Math.max(0, parsed),
                }));
              }}
            />
          </label>
          <label>
            조치(수리)처
            <input
              value={form.vendor ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, vendor: e.target.value }))}
            />
          </label>
          <div className="equip-tab-panel__form-actions">
            <button type="button" className="is-ghost" onClick={() => setShowForm(false)}>
              취소
            </button>
            <button type="submit" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      )}
      {loading ? (
        <p className="equip-tab-panel__hint">불러오는 중...</p>
      ) : rows.length === 0 ? (
        <p className="equip-tab-panel__hint">등록된 교체수리 이력이 없습니다.</p>
      ) : (
        <table className="equip-tab-panel__table">
          <thead>
            <tr>
              <th>일자</th>
              <th>부위</th>
              <th>내역</th>
              <th>보유부속품</th>
              <th style={{ width: '90px' }}>비용</th>
              <th>조치처</th>
              <th style={{ width: '120px' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.repairId}>
                <td>{row.repairDate || '-'}</td>
                <td>{row.partArea || '-'}</td>
                <td>{row.details || '-'}</td>
                <td>
                  {row.stockPartName
                    ? `${row.stockPartName}${row.usedQty ? ` ×${row.usedQty}` : ''}`
                    : '-'}
                </td>
                <td>{row.cost != null ? row.cost.toLocaleString() : '-'}</td>
                <td>{row.vendor || '-'}</td>
                <td>
                  <div className="equip-tab-panel__row-actions">
                    <button type="button" className="is-ghost" onClick={() => openEdit(row)}>
                      수정
                    </button>
                    <button type="button" className="is-danger" onClick={() => void handleDelete(row)}>
                      삭제
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default RepairsTab;
